/**
 * useDeviceDetect — single source of truth for device/environment detection.
 *
 * Detects:
 *   isCapacitor      — running inside Capacitor native shell
 *   isIOS / isAndroid
 *   isIPad           — iPad (including iPadOS 13+ which reports as MacIntel)
 *   isTablet         — iPad OR Android tablet (no "Mobile" in UA)
 *   isMobilePhone    — phone-sized device (not tablet)
 *   isMobileBrowser  — any mobile device accessed via a browser (not Capacitor)
 *   browserType      — 'chrome' | 'safari' | 'firefox' | 'other'
 *   hasBrowserChrome — browser has persistent UI chrome (addr bar, nav bar)
 *                      true for Chrome Android / Firefox; Safari collapses its bar
 *   isDesktop        — standard desktop/laptop
 *
 * Re-evaluates on orientation/resize so split between phone and tablet is live.
 */
import { useState, useEffect, useCallback } from 'react';

const detect = () => {
  const ua = navigator.userAgent;

  // ── Capacitor ──────────────────────────────────────────────────
  const isCapacitor =
    !!(window.Capacitor?.isNativePlatform?.()) ||
    !!window.Capacitor?.platform; // fallback for older Capacitor versions

  // ── OS ────────────────────────────────────────────────────────
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ pretends to be macOS desktop but has touch
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const isAndroid = /Android/i.test(ua);

  // ── iPad (iPadOS 13+ reports navigator.platform as 'MacIntel') ─
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // ── Tablet (large touch device) ───────────────────────────────
  // Android tablet: has Android UA but no "Mobile" keyword
  const isAndroidTablet = isAndroid && !/Mobile/i.test(ua);
  const isTablet = isIPad || isAndroidTablet;

  // ── Phone (small touch, not tablet) ───────────────────────────
  const isMobilePhone =
    !isTablet && (isIOS || isAndroid) && (
      /Mobi|Mobile/i.test(ua) || window.screen.width < 768
    );

  // ── Browser brand ─────────────────────────────────────────────
  const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|YaBrowser/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);
  const isFirefox = /Firefox|FxiOS/i.test(ua);
  const isChromeIOS = /CriOS/i.test(ua); // Chrome on iOS uses Webkit but adds CriOS

  const browserType = isChromeIOS
    ? 'chrome-ios'
    : isChrome
    ? 'chrome'
    : isSafari
    ? 'safari'
    : isFirefox
    ? 'firefox'
    : 'other';

  // ── Browser has persistent chrome (addr bar + nav bar) ────────
  // Chrome Android, Firefox Android, AND Chrome iOS all show persistent
  // browser UI that overlaps the viewport. Safari iOS collapses its bar on
  // scroll so we don't need the extra padding there.
  // Previously `hasBrowserChrome` was false for Chrome iOS, which meant the
  // layout didn't pad for the bottom toolbar and users couldn't reach content
  // behind it. Bug report 2026-04-19: "can't click the top back button" on
  // Chrome iOS because the button was hidden under the browser toolbar.
  const hasBrowserChrome =
    ((isChrome || isFirefox) && isAndroid) ||
    isChromeIOS;

  // ── In a browser (not Capacitor) ──────────────────────────────
  const isMobileBrowser = (isMobilePhone || isTablet) && !isCapacitor;

  // ── Desktop ───────────────────────────────────────────────────
  const isDesktop = !isMobilePhone && !isTablet && !isCapacitor;

  return {
    isCapacitor,
    isIOS,
    isAndroid,
    isIPad,
    isTablet,
    isMobilePhone,
    isMobileBrowser,
    hasBrowserChrome,
    browserType,
    isDesktop,
    windowWidth: window.innerWidth,
  };
};

export function useDeviceDetect() {
  const [device, setDevice] = useState(detect);

  // Re-detect on orientation/resize (phone ↔ tablet split can change)
  const handleResize = useCallback(() => {
    setDevice(detect());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [handleResize]);

  return device;
}
